import * as icons from '@mui/icons-material';
import { Chip, IconButton, Tooltip } from '@mui/material';
import { EditButton, List, useDataGrid } from '@refinedev/mui';
import React from 'react';
import { genAttributeSlug } from 'shiver-core/lib';
import { DataGrid, type GridColDef } from '../../../components/DataGrid.tsx';
import { getAppBaseUrl } from '../../../helpers/appUrl.ts';
import { useColumnVisibilityModel } from '../../../hooks/columnVisibilityModel.ts';
import type { Sector, TimelineHead, Topic } from '../../../types/index.ts';

export const TimelineList = () => {
  const { dataGridProps, filters, setFilters } = useDataGrid<TimelineHead>({
    resource: 'contents/timelines',
  });

  const columns = React.useMemo<Array<GridColDef<TimelineHead>>>(
    () => [
      {
        type: 'actions',
        field: 'actions',
        maxWidth: 80,
        renderCell({ row }) {
          const previewUrl = `${getAppBaseUrl()}/timeline/${genAttributeSlug(row.id)}`;
          return (
            <>
              <EditButton hideText recordItemId={row.id} />
              <Tooltip title="プレビュー">
                <IconButton
                  size="small"
                  onClick={() => {
                    window.open(previewUrl, '_blank', 'noopener,noreferrer');
                  }}
                >
                  <icons.Visibility fontSize="small" />
                </IconButton>
              </Tooltip>
            </>
          );
        },
      },
      {
        field: 'slug',
        headerName: 'slug',
        display: 'flex',
        minWidth: 200,
        valueGetter(_, row) {
          return genAttributeSlug(row.id);
        },
      },
      {
        field: 'name',
        flex: 2,
        headerName: 'タイトル',
        minWidth: 100,
      },
      {
        field: 'sectors',
        headerName: '分野',
        flex: 1,
        minWidth: 200,
        sortable: false,
        renderCell({ formattedValue, tabIndex }) {
          return (
            <div style={{ overflow: 'auto' }}>
              {formattedValue.map((item: Sector) => (
                <Chip
                  key={item.id}
                  label={item.name}
                  variant="outlined"
                  size="small"
                  tabIndex={tabIndex}
                />
              ))}
            </div>
          );
        },
      },
      {
        field: 'topics',
        headerName: 'トピック',
        flex: 1,
        minWidth: 200,
        sortable: false,
        renderCell({ formattedValue, tabIndex }) {
          return (
            <div style={{ overflow: 'auto' }}>
              {formattedValue.map((item: Topic) => (
                <Chip
                  key={item.id}
                  label={item.name}
                  variant="outlined"
                  size="small"
                  tabIndex={tabIndex}
                />
              ))}
            </div>
          );
        },
      },
      {
        type: 'dateTime',
        field: 'createdAt',
        headerName: '作成日時',
        display: 'flex',
        minWidth: 150,
        valueFormatter(_, { createdAt }) {
          if (!createdAt) return '-';
          return createdAt.toLocaleString('ja-JP').replace(/:\d{2}$/, '');
        },
      },
      {
        field: 'createdBy',
        headerName: '作成者',
        display: 'flex',
        minWidth: 150,
        valueGetter(_, row) {
          return row.createdBy ?? '-';
        },
      },
      {
        type: 'dateTime',
        field: 'modifiedAt',
        headerName: '変更日時',
        display: 'flex',
        minWidth: 150,
        valueFormatter(_, { modifiedAt }) {
          if (!modifiedAt) return '-';
          return modifiedAt.toLocaleString('ja-JP').replace(/:\d{2}$/, '');
        },
      },
      {
        field: 'modifiedBy',
        headerName: '変更者',
        display: 'flex',
        minWidth: 150,
        valueGetter(_, row) {
          return row.modifiedBy ?? '-';
        },
      },
    ],
    [],
  );

  const [columnVisibilityModel, setColumnVisibilityModel] =
    useColumnVisibilityModel('contents/timelines');

  return (
    <List>
      <DataGrid
        {...dataGridProps}
        columns={columns}
        filters={filters}
        setFilters={setFilters}
        filterDebounceMs={800}
        columnVisibilityModel={columnVisibilityModel}
        onColumnVisibilityModelChange={setColumnVisibilityModel}
      />
    </List>
  );
};
